import React, { useEffect, useState, useRef, useCallback } from "react";
import { Modal, Button } from "antd";
import {
  WarningOutlined,
  ClockCircleOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useSelector } from "react-redux";

/**
 * Component WaitError (Cảnh báo hồ sơ chưa được xác nhận khi người dùng không tương tác)
 *
 * @param {boolean} isConfirmed - Trạng thái hồ sơ đã được xác nhận chưa (true: đã xác nhận -> không hiện cảnh báo, false: chưa xác nhận -> theo dõi)
 * @param {number} timeout - Thời gian không hoạt động (ms) trước khi bật cảnh báo (mặc định: 60000ms = 1 phút)
 * @param {string} title - Tiêu đề Modal cảnh báo
 * @param {React.ReactNode} message - Nội dung cảnh báo tùy chỉnh
 * @param {string} okText - Nút tiếp tục thao tác
 * @param {string} cancelText - Nút đóng / để sau
 * @param {Function} onContinue - Callback khi người dùng bấm Tiếp tục
 * @param {Function} onTimeout - Callback khi kích hoạt trạng thái timeout
 * @param {boolean} disabled - Vô hiệu hóa việc theo dõi
 */
const WaitError = ({
  isConfirmed = false,
  timeout = 60000, // 1 phút mặc định
  title = "Cảnh báo: Hồ sơ chưa được xác nhận",
  message,
  okText = "Tiếp tục xử lý hồ sơ",
  cancelText = "Đóng",
  onContinue,
  onTimeout,
  disabled = false,
}) => {
  const General = useSelector((state) => state.General);
  const ShowUC = General?.ShowUC || false;

  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const lastActiveRef = useRef(Date.now());

  // Reset đếm ngược khi có tương tác
  const resetTimer = useCallback(() => {
    lastActiveRef.current = Date.now();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Nếu đã xác nhận hồ sơ, hoặc bị disabled, hoặc ShowUC không bật, hoặc modal đang mở thì không đặt timer
    if (isConfirmed || disabled || !ShowUC || open) {
      return;
    }

    timerRef.current = setTimeout(() => {
      // Khi hết thời gian timeout mà hồ sơ vẫn chưa được xác nhận và ShowUC đang bật
      if (!isConfirmed && !disabled && ShowUC) {
        setOpen(true);
        if (typeof onTimeout === "function") {
          onTimeout();
        }
      }
    }, timeout);
  }, [isConfirmed, disabled, ShowUC, open, timeout, onTimeout]);

  // Lắng nghe sự kiện tương tác của người dùng
  useEffect(() => {
    if (isConfirmed || disabled || !ShowUC) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setOpen(false);
      return;
    }

    // Bắt đầu đếm
    resetTimer();

    // Các sự kiện tương tác người dùng
    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    // Throttle để tối ưu hiệu năng
    let throttleTimeout = null;
    const handleUserActivity = () => {
      if (!throttleTimeout && !open) {
        throttleTimeout = setTimeout(() => {
          resetTimer();
          throttleTimeout = null;
        }, 1000);
      }
    };

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (throttleTimeout) clearTimeout(throttleTimeout);
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [isConfirmed, disabled, ShowUC, resetTimer, open]);

  // Xử lý khi bấm nút "Tiếp tục"
  const handleOk = () => {
    setOpen(false);
    resetTimer();
    if (typeof onContinue === "function") {
      onContinue();
    }
  };

  // Xử lý khi đóng
  const handleCancel = () => {
    setOpen(false);
    resetTimer();
  };

  if (isConfirmed || disabled || !ShowUC) {
    return null;
  }

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#faad14",
          }}
        >
          <ExclamationCircleFilled
            style={{ fontSize: "20px", color: "#faad14" }}
          />
          <span
            style={{ fontSize: "16px", fontWeight: "600", color: "#262626" }}
          >
            {title}
          </span>
        </div>
      }
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={okText}
      cancelText={cancelText}
      centered
      okButtonProps={{
        style: {
          backgroundColor: "#d32027",
          borderColor: "#d32027",
          color: "#ffffff",
          fontWeight: "600",
        },
      }}
      maskClosable={false}
      width={480}
    >
      <div
        style={{
          padding: "12px 0 6px 0",
          fontSize: "14px",
          color: "#555",
          lineHeight: "1.6",
        }}
      >
        {message ? (
          message
        ) : (
          <div>
            <p style={{ margin: "0 0 10px 0" }}>
              Bạn đang ở trên giao diện kê khai / xử lý hồ sơ nhưng{" "}
              <b>chưa thực hiện xác nhận nộp hồ sơ</b>.
            </p>
            <p style={{ margin: 0, color: "#8c8c8c", fontSize: "13px" }}>
              <ClockCircleOutlined style={{ marginRight: "6px" }} />
              Hệ thống phát hiện bạn không thao tác trong hơn{" "}
              {Math.round(timeout / 1000)} giây qua. Vui lòng kiểm tra lại thông
              tin và xác nhận để hoàn tất thủ tục.
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default WaitError;
